import { jsPDF } from "jspdf";
import { renderBrandedHeader, renderBrandedFooter, drawSignatureBlock } from "@/lib/pdfBranding";

const COLORS = {
  text: [26, 28, 26],
  muted: [110, 115, 110],
  border: [209, 209, 203],
};

/**
 * Genera un PDF con la evolución clínica seleccionada, incluyendo firma del odontólogo
 * y datos básicos del paciente. La firma se toma de evolution.doctor.signature_image.
 */
export function generateEvolutionPdf({ evolution, patient, branding }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 48;
  const doctor = evolution.doctor || {};

  let y = renderBrandedHeader(doc, branding, {
    title: "EVOLUCIÓN CLÍNICA",
    subtitle: evolution.fecha || "",
  });
  y += 6;

  // Paciente
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Paciente", margin, y); y += 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(patient?.name || "—", margin, y); y += 12;
  if (patient?.document_number) {
    doc.setTextColor(...COLORS.muted);
    doc.text(`Doc: ${patient.document_type || "CC"} ${patient.document_number}`, margin, y); y += 12;
  }
  if (patient?.phone) { doc.setTextColor(...COLORS.muted); doc.text(`Tel: ${patient.phone}`, margin, y); y += 12; }
  if (patient?.eps) { doc.setTextColor(...COLORS.muted); doc.text(`EPS: ${patient.eps}`, margin, y); y += 12; }

  y += 10;

  // Profesional
  doc.setTextColor(...COLORS.text); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Profesional", margin, y); y += 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(doctor.name || "—", margin, y); y += 12;
  if (doctor.specialty) { doc.setTextColor(...COLORS.muted); doc.text(doctor.specialty, margin, y); y += 12; }
  if (doctor.registry_number) { doc.setTextColor(...COLORS.muted); doc.text(`Registro: ${doctor.registry_number}`, margin, y); y += 12; }

  y += 16;

  // Detalles de la evolución
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Datos de la atención", margin, y); y += 14;

  const fields = [
    ["Fecha", evolution.fecha || "—"],
    ["Diente (FDI)", evolution.diente || "—"],
    ["Diagnóstico", evolution.diagnostico || "—"],
  ];
  doc.setFontSize(9);
  fields.forEach(([k, v]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.muted);
    doc.text(k.toUpperCase(), margin, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.text);
    const wrapped = doc.splitTextToSize(String(v), W - margin - (margin + 110));
    doc.text(wrapped, margin + 110, y);
    y += Math.max(14, wrapped.length * 12);
  });

  y += 8;

  // Procedimiento
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...COLORS.text);
  doc.text("Procedimiento realizado", margin, y); y += 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  const proc = doc.splitTextToSize(evolution.procedimiento || "—", W - 2 * margin);
  doc.text(proc, margin, y); y += proc.length * 13 + 8;

  // Observaciones
  if (evolution.observaciones) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...COLORS.text);
    doc.text("Observaciones", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const obs = doc.splitTextToSize(evolution.observaciones, W - 2 * margin);
    doc.text(obs, margin, y); y += obs.length * 13;
  }

  // Firma del profesional al final de la página
  const sigBaseY = H - 110;
  drawSignatureBlock(doc, {
    x: margin + 220,
    baselineY: sigBaseY,
    width: W - margin - (margin + 220),
    signatureImage: doctor.signature_image || null,
    label: "Firma del profesional",
    name: doctor.name || "",
    subtitle: doctor.signature_text || doctor.registry_number || "",
  });

  // Aviso legal
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, H - 70, W - margin, H - 70);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...COLORS.muted);
  doc.text(
    "Documento clínico generado electrónicamente. La historia clínica del paciente reposa en la institución.",
    margin, H - 54
  );
  renderBrandedFooter(doc, branding);

  const safeName = (patient?.name || "paciente").replace(/\s+/g, "_");
  const fname = `evolucion_${safeName}_${evolution.fecha || "sin_fecha"}.pdf`;
  doc.save(fname);
}
